/**
 * TeamSystem Impact - Main App Component
 */

import { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { I18nextProvider } from 'react-i18next';

import { lightTheme, darkTheme } from './theme';
import { useThemeStore } from './store/themeStore';
import i18n from './i18n/config';
import { msalConfig } from './config/msalConfig';
import { AppRoutes } from './AppRoutes';

// Inizializza MSAL
const msalInstance = new PublicClientApplication(msalConfig);

// Inizializza React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minuti
    },
  },
});

export const App = () => {
  const resolvedTheme = useThemeStore((s) => s.resolvedTheme)();
  const mode = useThemeStore((s) => s.mode);

  // Sync dark class on <html> and listen for system theme changes
  useEffect(() => {
    document.documentElement.classList.toggle('dark', resolvedTheme === 'dark');
  }, [resolvedTheme]);

  useEffect(() => {
    if (mode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      document.documentElement.classList.toggle('dark', mq.matches);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [mode]);

  return (
    <MsalProvider instance={msalInstance}>
      <QueryClientProvider client={queryClient}>
        <I18nextProvider i18n={i18n}>
          <ThemeProvider theme={resolvedTheme === 'dark' ? darkTheme : lightTheme}>
            <CssBaseline />
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </ThemeProvider>
        </I18nextProvider>
      </QueryClientProvider>
    </MsalProvider>
  );
};
